<template>
  <FormGroup
    :label="label"
    :class="{ 'guests-field--over-capacity': isOverCapacity }"
    :data-test-id="dataTestId + '__label'"
  >
    <TextField
      ref="textField"
      :id="id"
      :model-value="modelValue"
      :icon="PeopleIcon"
      :placeholder="placeholder"
      :errors="errors"
      :is-marked-as-invalid="errors.length > 0"
      :data-test-id="dataTestId"
      :min="0"
      :suffix-length="hasCursor ? 36 : undefined"
      type="number"
      inputmode="numeric"
      pattern="\d*"
      @update:modelValue="emit('update:modelValue', $event)"
      @click="onFieldClick"
      @change="emit('change')"
    >
      <template v-if="hasCursor" #suffixIcon>
        <div class="flex flex-col mr-1">
          <button
            type="button"
            class="flex p-0 pb-px border-0 bg-transparent cursor-pointer text-grey-500 hover:text-grey-800"
            tabindex="-1"
            aria-label="Increase number of guests"
            data-guests-arrow
            @click="step('up')"
          >
            <ChevronUpIcon class="w-3 h-3" />
          </button>
          <button
            type="button"
            class="flex p-0 pt-px border-0 bg-transparent cursor-pointer text-grey-500 hover:text-grey-800"
            tabindex="-1"
            aria-label="Decrease number of guests"
            data-guests-arrow
            @click="step('down')"
          >
            <ChevronDownIcon class="w-3 h-3" />
          </button>
        </div>
      </template>
    </TextField>

    <FormCaption v-if="isOverCapacity" class="guests-field__warning mt-1">
      <AlertTriangleIcon />
      <span>Max {{ maxCapacity }} guests</span>
    </FormCaption>
  </FormGroup>
</template>

<script setup>
import AlertTriangleIcon from '@tagvenue/icons/24/bold/alerts-feedback/alert-triangle.svg';
import ChevronDownIcon from '@tagvenue/icons/24/line/arrows/chevron-down.svg';
import ChevronUpIcon from '@tagvenue/icons/24/line/arrows/chevron-up.svg';
import PeopleIcon from '@tagvenue/icons/24/line/users/users-03.svg';
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

import FormCaption from '@/components/Ui/Forms/FormCaption.vue';
import FormGroup from '@/components/Ui/Forms/FormGroup.vue';
import TextField from '@/components/Ui/Forms/TextField.vue';
import inputHandlers from '@/services/inputHandlers/index.js';

const props = defineProps({
  modelValue: {
    type: [Number, String],
    required: true,
  },
  errors: {
    type: Array,
    required: true,
  },
  maxCapacity: {
    type: Number,
    required: true,
  },
  id: {
    type: String,
    default: 'people',
  },
  dataTestId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'click', 'change', 'arrowClick']);

const store = useStore();
const textField = ref(null);

const input = () => textField.value.$el.querySelector('input');

// The arrows are too small to tap, so touch devices get the plain input
const hasCursor = window.matchMedia('(pointer: fine)').matches;

// Weddings already ask for an estimated number of guests; only the generic "People" changes.
const label = computed(() => (store.getters.isWedding ? store.getters.eventTypeSpecificCopy.people : 'Number of guests'));

const placeholder = computed(() => {
  const { standing, sitting } = store.state.room.capacity;
  const exampleCapacity = standing || sitting || props.maxCapacity;

  return exampleCapacity ? `eg. ${exampleCapacity}` : '';
});

const isOverCapacity = computed(() => Boolean(props.maxCapacity) && parseInt(props.modelValue, 10) > props.maxCapacity);

// The native stepping keeps the arrows in line with the keyboard's up/down, including the min boundary.
const step = (direction) => {
  if (direction === 'up') {
    input().stepUp();
  } else {
    input().stepDown();
  }

  emit('update:modelValue', input().value);
  emit('change');
  emit('arrowClick', { direction, value: input().value });
};

// Arrow clicks bubble up to the input wrapper, but they're tracked as their own elements, not as a field click.
const onFieldClick = (event) => {
  if (!event.target.closest('[data-guests-arrow]')) {
    emit('click');
  }
};

onMounted(() => {
  inputHandlers.number(input());
});
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;

.guests-field--over-capacity :deep(input) {
  border-color: ds-variables.$yellow;
}

.guests-field__warning {
  display: flex;
  align-items: center;
  color: ds-variables.$yellow;

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-right: 4px;
  }
}
</style>
